import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import appCss from "../styles.css?url";
import { reportLovableError } from "../lib/lovable-error-reporting";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-extrabold text-gradient-rainbow">404</h1>
        <h2 className="mt-4 text-xl font-extrabold">Pagina niet gevonden</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Deze pagina bestaat niet of is verplaatst.
        </p>
        <Link to="/" className="btn-cta mt-6">Terug naar home</Link>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-extrabold">Er ging iets mis</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Probeer opnieuw of ga terug naar de homepagina.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => { router.invalidate(); reset(); }}
            className="btn-cta"
          >
            Opnieuw proberen
          </button>
          <a href="/" className="btn-cta-inverse border border-border">Home</a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "Hi Coach — Beweegavontuur voor kinderen van 2 tot 6 jaar" },
      { name: "description", content: "Elke zondagochtend nemen Sem en Maya jouw kind mee op een beweegavontuur in Rhoon. Brede motorische basis via de ASM-methode." },
      { name: "author", content: "Hi Coach" },
      { property: "og:title", content: "Hi Coach — Beweegavontuur voor kinderen van 2 tot 6 jaar" },
      { property: "og:description", content: "Elke zondagochtend nemen Sem en Maya jouw kind mee op een beweegavontuur in Rhoon. Brede motorische basis via de ASM-methode." },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "Hi Coach" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Hi Coach — Beweegavontuur voor kinderen van 2 tot 6 jaar" },
      { name: "twitter:description", content: "Elke zondagochtend nemen Sem en Maya jouw kind mee op een beweegavontuur in Rhoon. Brede motorische basis via de ASM-methode." },
      { property: "og:image", content: "https://storage.googleapis.com/gpt-engineer-file-uploads/attachments/og-images/7f07f843-2167-495f-89ae-137d646d88d4" },
      { name: "twitter:image", content: "https://storage.googleapis.com/gpt-engineer-file-uploads/attachments/og-images/7f07f843-2167-495f-89ae-137d646d88d4" },
    ],
    links: [{ rel: "stylesheet", href: appCss }],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="nl">
      <head><HeadContent /></head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  return (
    <QueryClientProvider client={queryClient}>
      <Outlet />
    </QueryClientProvider>
  );
}
